import type {
  Address,
  Instruction,
  AccountMeta,
  AccountSignerMeta,
  ReadonlyUint8Array,
} from '@solana/kit';
import {
  AccountRole,
  getProgramDerivedAddress,
  getAddressEncoder,
} from '@solana/kit';
import {
  SYSTEM_PROGRAM_ADDRESS,
  SYSVAR_INSTRUCTIONS_ADDRESS,
  TOKEN_2022_PROGRAM_ADDRESS,
  TOKEN_PROGRAM_ADDRESS,
  TOKEN_METADATA_PROGRAM_ID,
} from '../../core/constants.js';
import {
  createAccountMeta,
  getAddressFromAddressOrSigner,
  isTransactionSigner,
  type AddressOrTransactionSigner,
} from '../../core/accounts.js';
import {
  CURVE_KIND_XYK,
  CURVE_PARAMS_FORMAT_XYK_V0,
  INITIALIZER_PROGRAM_ID,
  HF_AFTER_CREATE,
  HF_BEFORE_CREATE,
} from '../constants.js';
import { computeRemainingAccountsHash } from '../helpers.js';
import { CPMM_MIGRATOR_PROGRAM_ID } from '../../migrators/cpmmMigrator/constants.js';
import { getCpmmMigratorStateAddress } from '../../migrators/cpmmMigrator/pda.js';
import { PREDICTION_MIGRATOR_PROGRAM_ADDRESS } from '../../generated/predictionMigrator/programs/predictionMigrator.js';
import {
  getPredictionMarketAddress,
  getPredictionMarketAuthorityAddress,
  getPredictionPotVaultAddress,
  getPredictionEntryAddress,
  getPredictionEntryByMintAddress,
} from '../../migrators/predictionMigrator/pda.js';
import { getLaunchFeeStateAddress } from '../pda.js';
import type { InitializeLaunchArgsArgs } from '../../generated/initializer/index.js';
import { getInitializeLaunchInstructionDataEncoder } from '../../generated/initializer/index.js';

/**
 * Public params for createInitializeLaunchInstruction.
 * - allowBuy / allowSell are boolean; converted to u8 wire format (1/0).
 * - migratorProgram is omitted — it is derived from accounts.migratorProgram
 *   so callers do not need to repeat it in both the accounts object and args.
 */
export type InitializeLaunchParams = Omit<
  InitializeLaunchArgsArgs,
  | 'allowBuy'
  | 'allowSell'
  | 'hookProgram'
  | 'migratorProgram'
  | 'hookCreateRemainingAccountsLen'
  | 'hookCreateRemainingAccountsHash'
  | 'swapFeeBps'
  | 'feeBeneficiaries'
> & {
  allowBuy: boolean;
  allowSell: boolean;
  swapFeeBps?: number;
  /** @deprecated Use swapFeeBps. */
  curveFeeBps?: number;
  feeBeneficiaries?: InitializeLaunchArgsArgs['feeBeneficiaries'];
  hookCreateRemainingAccountsLen?: number;
  hookCreateRemainingAccountsHash?: ReadonlyUint8Array;
};

type AddressOrSigner = AddressOrTransactionSigner;
type ReadonlyRemainingAccount = AddressOrSigner;

/**
 * Derive the Metaplex token metadata PDA for a given mint.
 * Seeds: ["metadata", TOKEN_METADATA_PROGRAM_ID, mint]
 *
 * Pass the result as `metadataAccount` in InitializeLaunchAccounts
 * whenever `metadataName` is non-empty.
 */
export async function getTokenMetadataAddress(mint: Address): Promise<Address> {
  const encoder = getAddressEncoder();
  const [metadataAddress] = await getProgramDerivedAddress({
    programAddress: TOKEN_METADATA_PROGRAM_ID,
    seeds: [
      new TextEncoder().encode('metadata'),
      encoder.encode(TOKEN_METADATA_PROGRAM_ID),
      encoder.encode(mint),
    ],
  });
  return metadataAddress;
}

export interface InitializeLaunchAccounts {
  config: Address;
  launch: Address;
  launchAuthority: Address;
  baseMint: AddressOrSigner;
  quoteMint: Address;
  baseVault: AddressOrSigner;
  quoteVault: AddressOrSigner;
  launchFeeState?: Address;
  payer: AddressOrSigner;
  authority?: AddressOrSigner;
  hookProgram?: Address;
  migratorProgram?: Address;
  baseTokenProgram?: Address;
  quoteTokenProgram?: Address;
  systemProgram?: Address;
  rent: Address;
  /** Required when args.metadataName is non-empty. Derive with getTokenMetadataAddress(baseMint). */
  metadataAccount?: Address;
  metadataProgram?: Address;
  /** Immutable vesting config committed to this launch. */
  vestingConfig?: Address;
  /** Required when migratorProgram is the CPMM migrator. */
  cpmmConfig?: Address;
  /**
   * Remaining accounts committed to initialize_launch create hooks.
   * These are forwarded as readonly metas; TransactionSigner values are
   * forwarded as readonly signers.
   */
  hookCreateRemainingAccounts?: ReadonlyArray<ReadonlyRemainingAccount>;
}

function validateInitializeLaunchCurveParams(
  args: InitializeLaunchParams,
): void {
  if (args.curveKind !== CURVE_KIND_XYK) {
    throw new Error(
      `unsupported curve kind: ${args.curveKind}; only CURVE_KIND_XYK is currently enabled`,
    );
  }
  if (
    args.curveParams.length !== 1 ||
    args.curveParams[0] !== CURVE_PARAMS_FORMAT_XYK_V0
  ) {
    throw new Error('xyk curve params must be [CURVE_PARAMS_FORMAT_XYK_V0]');
  }
}

export async function createInitializeLaunchInstruction(
  accounts: InitializeLaunchAccounts,
  args: InitializeLaunchParams,
  programId: Address = INITIALIZER_PROGRAM_ID,
): Promise<Instruction> {
  validateInitializeLaunchCurveParams(args);

  const {
    config,
    launch,
    launchAuthority,
    baseMint,
    quoteMint,
    baseVault,
    quoteVault,
    launchFeeState,
    payer,
    authority,
    hookProgram,
    migratorProgram,
    baseTokenProgram = TOKEN_PROGRAM_ADDRESS,
    quoteTokenProgram = TOKEN_PROGRAM_ADDRESS,
    systemProgram = SYSTEM_PROGRAM_ADDRESS,
    rent,
    metadataAccount,
    metadataProgram = TOKEN_METADATA_PROGRAM_ID,
    vestingConfig,
    hookCreateRemainingAccounts = [],
  } = accounts;

  const withMetadata = Boolean(
    args.metadataName && args.metadataName.length > 0,
  );
  const withToken2022Metadata =
    withMetadata && baseTokenProgram === TOKEN_2022_PROGRAM_ADDRESS;

  if (withMetadata && !metadataAccount) {
    throw new Error(
      'metadataName is set but metadataAccount was not provided. ' +
        'Derive it with await initializer.getTokenMetadataAddress(baseMintAddress).',
    );
  }

  const createHooksEnabled =
    (args.hookFlags & (HF_BEFORE_CREATE | HF_AFTER_CREATE)) !== 0;
  const hookCreateRemainingAccountAddresses = hookCreateRemainingAccounts.map(
    getAddressFromAddressOrSigner,
  );
  const swapFeeBps = args.swapFeeBps ?? args.curveFeeBps;
  if (swapFeeBps === undefined) {
    throw new Error('swapFeeBps is required');
  }

  const keys: (AccountMeta | AccountSignerMeta)[] = [
    { address: config, role: AccountRole.READONLY },
    { address: launch, role: AccountRole.WRITABLE },
    { address: launchAuthority, role: AccountRole.READONLY },
    createAccountMeta(baseMint, AccountRole.WRITABLE_SIGNER),
    { address: quoteMint, role: AccountRole.READONLY },
    createAccountMeta(baseVault, AccountRole.WRITABLE_SIGNER),
    createAccountMeta(quoteVault, AccountRole.WRITABLE_SIGNER),
    {
      address:
        launchFeeState ??
        (await getLaunchFeeStateAddress(launch, programId))[0],
      role: AccountRole.WRITABLE,
    },
    createAccountMeta(payer, AccountRole.WRITABLE_SIGNER),
  ];

  keys.push(
    authority
      ? createAccountMeta(authority, AccountRole.READONLY_SIGNER)
      : { address: programId, role: AccountRole.READONLY },
  );
  keys.push(
    hookProgram
      ? { address: hookProgram, role: AccountRole.READONLY }
      : { address: programId, role: AccountRole.READONLY },
  );
  keys.push(
    migratorProgram
      ? { address: migratorProgram, role: AccountRole.READONLY }
      : { address: programId, role: AccountRole.READONLY },
  );

  keys.push({ address: baseTokenProgram, role: AccountRole.READONLY });
  keys.push({ address: quoteTokenProgram, role: AccountRole.READONLY });
  keys.push({ address: systemProgram, role: AccountRole.READONLY });
  keys.push({ address: rent, role: AccountRole.READONLY });

  if (withMetadata) {
    keys.push({ address: metadataAccount!, role: AccountRole.WRITABLE });
    keys.push({ address: metadataProgram, role: AccountRole.READONLY });
  } else {
    keys.push({ address: programId, role: AccountRole.READONLY });
    keys.push({ address: programId, role: AccountRole.READONLY });
  }

  // Older Initializer deployments do not have the optional vesting account.
  // A supplied config identifies the current layout for custom deployments.
  if (programId === INITIALIZER_PROGRAM_ID || vestingConfig) {
    keys.push(
      vestingConfig
        ? { address: vestingConfig, role: AccountRole.READONLY }
        : { address: programId, role: AccountRole.READONLY },
    );
  }

  const encoderArgs: InitializeLaunchArgsArgs = {
    ...args,
    swapFeeBps,
    feeBeneficiaries: args.feeBeneficiaries ?? [],
    allowBuy: args.allowBuy ? 1 : 0,
    allowSell: args.allowSell ? 1 : 0,
    hookCreateRemainingAccountsLen:
      args.hookCreateRemainingAccountsLen ?? hookCreateRemainingAccounts.length,
    hookCreateRemainingAccountsHash:
      args.hookCreateRemainingAccountsHash ??
      (createHooksEnabled
        ? computeRemainingAccountsHash(hookCreateRemainingAccountAddresses)
        : new Uint8Array(32)),
  };

  const data = new Uint8Array(
    getInitializeLaunchInstructionDataEncoder().encode(encoderArgs),
  );

  if (withToken2022Metadata) {
    keys.push({
      address: SYSVAR_INSTRUCTIONS_ADDRESS,
      role: AccountRole.READONLY,
    });
  }

  keys.push(
    ...hookCreateRemainingAccounts.map((account) =>
      createAccountMeta(
        account,
        isTransactionSigner(account)
          ? AccountRole.READONLY_SIGNER
          : AccountRole.READONLY,
      ),
    ),
  );

  // When using the CPMM migrator, append the module-specific accounts required
  // by register_launch. Custom deployments identify this path by passing
  // cpmmConfig alongside their custom migrator program ID.
  const shouldAppendCpmmMigratorAccounts =
    accounts.cpmmConfig !== undefined ||
    migratorProgram === CPMM_MIGRATOR_PROGRAM_ID;
  if (shouldAppendCpmmMigratorAccounts) {
    if (!migratorProgram) {
      throw new Error(
        'migratorProgram is required when cpmmConfig is provided',
      );
    }
    if (!accounts.cpmmConfig) {
      throw new Error('cpmmConfig is required when using the CPMM migrator');
    }
    const [cpmmMigrationState] = await getCpmmMigratorStateAddress(
      launch,
      migratorProgram,
    );
    keys.push({ address: cpmmMigrationState, role: AccountRole.WRITABLE });
    keys.push({ address: accounts.cpmmConfig, role: AccountRole.READONLY });
  }

  // When using the prediction migrator, automatically derive and append the 6
  // remaining accounts required by register_entry:
  //   [oracle_state, market, pot_vault, market_authority, entry, entry_by_mint]
  // namespace === oracle_state and launchId === entryId per program validation.
  if (migratorProgram === PREDICTION_MIGRATOR_PROGRAM_ADDRESS) {
    const oracleState = args.namespace as Address;
    const entryId = args.launchId;
    const baseMintAddress = getAddressFromAddressOrSigner(baseMint);

    const [market] = await getPredictionMarketAddress(oracleState, quoteMint);
    const [potVault] = await getPredictionPotVaultAddress(market);
    const [marketAuthority] = await getPredictionMarketAuthorityAddress(market);
    const [entry] = await getPredictionEntryAddress(market, entryId);
    const [entryByMint] = await getPredictionEntryByMintAddress(
      market,
      baseMintAddress,
    );

    keys.push({ address: oracleState, role: AccountRole.READONLY });
    keys.push({ address: market, role: AccountRole.WRITABLE });
    keys.push({ address: potVault, role: AccountRole.WRITABLE });
    keys.push({ address: marketAuthority, role: AccountRole.READONLY });
    keys.push({ address: entry, role: AccountRole.WRITABLE });
    keys.push({ address: entryByMint, role: AccountRole.WRITABLE });
  }

  return { programAddress: programId, accounts: keys, data };
}
